/**
 * Soft!=Live Page204 — Settings OTA residual liquid (Page158/103/49 + dual).
 * Mount: /settings/ota
 * Page158 Soft residual retained on this desk residual pins.
 * Page204 Soft residual dual lifecycle || publish.
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_ota_status · get__ota_campaigns · get__ota_firmwares · policy
 *   accept · decline · rollback · cancel · from-stored
 * Soft residual deleted:
 *  - Soft Accept-only theatre
 *  - Soft invent campaigns when lifecycle sealed
 *  - Soft Publish when publish bus sealed
 *  - Soft policy PATCH toggles
 *  - client invent of seats while posture loading
 *  - path /operation labels painted in DOM
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { campaignRowsOf, firmwareRowsOf } from "./fleet-live.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { actionReceiptOf, isWriteDeniedStatus } from "./cap-complete.js";

const CAMPAIGN_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "accepted", label: "Accepted" },
  { id: "declined", label: "Declined" },
  { id: "cancelled", label: "Cancelled" },
  { id: "rolled_back", label: "Rolled back" },
];

/** Path-free operator copy for Base image failures. Catalogue stays empty until the server returns rows. */
export function softSettingsOtaErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|ota_seat|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot run board OTA here. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|campaign/.test(low)) {
    return "That campaign or build is not available.";
  }
  if (/\b422\b|\b409\b|invalid|required|conflict|filename/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Base image updates are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/ota\/|\/settings\/ota|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft≠Live Page204: missing contract ops must not crash the desk. */
function safeOtaPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_ota_status") return "/settings/ota/status";
    if (op === "get__ota_status") return "/ota/status";
    if (op === "get__ota_campaigns") return "/ota/campaigns";
    if (op === "get__ota_firmwares") return "/ota/firmwares";
    if (op === "get__ota_policy") return "/ota/policy";
    if (op === "post__ota_campaigns_from-stored") return "/ota/campaigns/from-stored";
    if (op === "post__ota_campaigns_p_accept") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/ota/campaigns/" + encodeURIComponent(id) + "/accept" : "";
    }
    if (op === "post__ota_campaigns_p_decline") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/ota/campaigns/" + encodeURIComponent(id) + "/decline" : "";
    }
    if (op === "post__ota_campaigns_p_rollback") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/ota/campaigns/" + encodeURIComponent(id) + "/rollback" : "";
    }
    if (op === "post__ota_campaigns_p_cancel") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/ota/campaigns/" + encodeURIComponent(id) + "/cancel" : "";
    }
    return "";
  }
}

/** Pure: seat chips from Live Settings OTA posture (Page204) — fail-closed. */
export function otaSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const campMax = Number(lim.campaign_paint_max);
  const buildMax = Number(lim.build_paint_max);
  /* Fail-closed: BE asserts each plane — no null inherit, no list→publish invent. */
  return {
    lifecycleAllowed: p.campaign_lifecycle_allowed === true,
    writeAllowed: p.campaign_write_allowed === true,
    publishAllowed: p.firmware_publish_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    publicBase: p.public_base_configured === true,
    token: p.token_configured === true,
    postureLabel: String(p.posture_label || "").trim(),
    campaignPaintMax:
      Number.isFinite(campMax) && campMax > 0 ? Math.floor(campMax) : 80,
    buildPaintMax:
      Number.isFinite(buildMax) && buildMax > 0 ? Math.floor(buildMax) : 40,
    fanoutAllowed: !!(p.verb_matrix && p.verb_matrix.rail_b && p.verb_matrix.rail_b.accept === true),
    consentAllowed: !!(p.verb_matrix && p.verb_matrix.rail_b && p.verb_matrix.rail_b.consent === true),
  };
}

function _capList(me) {
  const raw = (me && (me.caps || me.sa_capabilities)) || [];
  return Array.isArray(raw) ? raw.map((c) => String(c || "").trim()) : String(raw).split(",").map((c) => c.trim()).filter(Boolean);
}

/** Pure: OTA campaign list grant (admin+) — exact bits; Live posture SoT. */
export function canReadOtaCampaigns(me) {
  if (!me) return false;
  const role = String(me.role || "").toLowerCase();
  if (role === "admin" || role === "superadmin") return true;
  if (role !== "sa_staff") return false;
  const caps = _capList(me);
  return caps.includes("sa_ota_admin") || caps.includes("sa_full_access");
}

/** Pure: firmware inventory + from-stored publish (SA OTA). */
export function canPublishOta(me) {
  if (!me) return false;
  const role = String(me.role || "").toLowerCase();
  if (role === "superadmin") return true;
  if (role !== "sa_staff") return false;
  const caps = _capList(me);
  return caps.includes("sa_ota_admin") || caps.includes("sa_full_access");
}

/** Pure: policy envelope for KPI strip. */
export function policyViewOf(pack) {
  const p = pack && typeof pack === "object" ? pack : {};
  return {
    autoRb: !!p.auto_rollback_on_failure,
    ackS: p.device_ack_timeout_seconds != null ? Number(p.device_ack_timeout_seconds) : null,
    hw: !!p.hw_compat_enabled,
    reads: !!p.campaign_reads,
    writes: !!p.campaign_writes,
    base: !!p.public_base_configured,
    token: !!p.token_configured,
    note: String(p.note || "").trim(),
  };
}

export function OtaSettingsDesk({ toast }) {
  const { _me } = useSession();
  const posture = useApi(safeOtaPath("get__settings_ota_status"));
  const post = posture.data || {};
  // Fail-closed while loading — Soft client invent seats deleted.
  const seat = otaSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const otaOk = seat.lifecycleAllowed === true;
  const canPub = seat.publishAllowed === true;
  const _detail = seat.detail;
  const postureLabel = seat.postureLabel
    || (otaOk
      ? (canPub ? "Campaign and publish desks open" : "Campaign desk open · publish sealed")
      : "Base image settings need an admin seat");

  const campsLive = useApi(otaOk ? safeOtaPath("get__ota_campaigns") : null);
  const buildsLive = useApi(canPub ? safeOtaPath("get__ota_firmwares") : null);
  const policyLive = useApi(otaOk ? (safeOtaPath("get__ota_policy") || "/ota/policy") : null);
  const act = useAction();
  const envelope = act.envelope;
  const campsAll = useMemo(() => campaignRowsOf(campsLive.data), [campsLive.data]);
  const camps = useMemo(
    () => campsAll.slice(0, seat.campaignPaintMax),
    [campsAll, seat.campaignPaintMax],
  );
  const buildsAll = useMemo(() => firmwareRowsOf(buildsLive.data), [buildsLive.data]);
  const builds = useMemo(
    () => buildsAll.slice(0, seat.buildPaintMax),
    [buildsAll, seat.buildPaintMax],
  );
  const pol = useMemo(() => {
    if (policyLive.data && typeof policyLive.data === "object") {
      return policyViewOf(policyLive.data);
    }
    return policyViewOf({
      auto_rollback_on_failure: post.auto_rollback_on_failure,
      device_ack_timeout_seconds: post.device_ack_timeout_seconds,
      hw_compat_enabled: post.hw_compat_enabled,
      campaign_reads: seat.lifecycleAllowed,
      campaign_writes: seat.writeAllowed,
      public_base_configured: seat.publicBase,
      token_configured: seat.token,
    });
  }, [policyLive.data, post, seat]);
  const [filter, setFilter] = useState("all");
  const [pubFile, setPubFile] = useState("");
  const [pubNotes, setPubNotes] = useState("");
  const [pubFw, setPubFw] = useState("");

  const filtered = filter === "all" ? camps : camps.filter((c) => c.state === filter);
  const count = (id) => camps.filter((c) => c.state === id).length;

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    campsLive.reload && campsLive.reload();
    buildsLive.reload && buildsLive.reload();
    policyLive.reload && policyLive.reload();
    toast && toast("Base image status refreshed", "go");
  };

  const liveWrite = async (kind, id) => {
    if (!seat.writeAllowed) {
      toast && toast("Your seat cannot change campaigns.", "warn");
      return;
    }
    if (!id) return;
    const ok = await confirmDeviceCommand({
      kind: "ota_" + kind,
      targets: [{ id: String(id), label: "Board campaign " + String(id) }],
      detail: kind === "accept"
        ? "Accepts this board firmware campaign for dispatch. Device confirm is separate from Director accept."
        : kind === "rollback"
          ? "Starts a rollback of this board campaign."
          : "Declines this board campaign.",
      confirmLabel: kind === "accept" ? "Accept campaign" : kind === "rollback" ? "Roll back" : "Decline",
    });
    if (!ok) return;
    const op =
      kind === "accept" ? "post__ota_campaigns_p_accept"
        : kind === "decline" ? "post__ota_campaigns_p_decline"
          : kind === "rollback" ? "post__ota_campaigns_p_rollback"
            : null;
    if (!op) return;
    const out = await act.run(safeOtaPath(op, { pathParams: [id] }), { method: "POST", body: {} });
    if (!out) {
      toast(softSettingsOtaErrorOf(act.error), isWriteDeniedStatus(act.error) ? "warn" : "bad");
      return;
    }
    const lab = kind === "accept" ? "accepted" : kind === "decline" ? "declined" : "rolled back";
    if (kind === "accept") {
      const ack = out && out.device_ack_observed === true;
      const verified = out && (out.verified === true || out.accept_claims_verified === true);
      if (out && out.fanout === false) {
        toast(actionReceiptOf(out, "Consent recorded · Base needs OTA admin"), "go");
      } else if (ack || verified) {
        toast(actionReceiptOf(out, "Campaign accepted · device confirmed"), "go");
      } else {
        toast(actionReceiptOf(out, "Commands sent · waiting for device confirm"), "warn");
      }
    } else {
      toast(actionReceiptOf(out, "Campaign " + lab), "go");
    }
    campsLive.reload && campsLive.reload();
  };

  const cancel = async (id) => {
    if (!seat.writeAllowed) {
      toast && toast("Your seat cannot change campaigns.", "warn");
      return;
    }
    if (!id) return;
    const ok = await confirmDeviceCommand({
      kind: "ota_cancel",
      targets: [{ id: String(id), label: "Board campaign " + String(id) }],
      detail: "Cancels this board campaign. This is a real change.",
      confirmLabel: "Cancel campaign",
    });
    if (!ok) return;
    let url = safeOtaPath("post__ota_campaigns_p_cancel", { pathParams: [id] });
    if (!url) url = "/ota/campaigns/" + encodeURIComponent(id) + "/cancel";
    const out = await act.run(url, { method: "POST", body: {} });
    if (!out) {
      toast(softSettingsOtaErrorOf(act.error), "bad");
      return;
    }
    toast("Campaign cancelled", "warn");
    campsLive.reload && campsLive.reload();
  };

  const publishStored = async () => {
    if (!seat.publishAllowed) {
      toast && toast("Your seat cannot publish board builds.", "warn");
      return;
    }
    const filename = String(pubFile || "").trim();
    if (!filename) {
      toast("Pick a staged firmware file", "warn");
      return;
    }
    const ok = await confirmDeviceCommand({
      kind: "ota_publish",
      targets: [{ id: filename, label: filename }],
      detail: "Publishes this staged board build as a campaign for target admins.",
      confirmLabel: "Publish campaign",
    });
    if (!ok) return;
    const body = { filename, target_admins: ["*"] };
    if (pubNotes.trim()) body.notes = pubNotes.trim();
    if (pubFw.trim()) body.fw_version = pubFw.trim();
    const out = await act.run(safeOtaPath("post__ota_campaigns_from-stored"), { method: "POST", body });
    if (!out) {
      toast(softSettingsOtaErrorOf(act.error), isWriteDeniedStatus(act.error) ? "warn" : "bad");
      return;
    }
    toast(actionReceiptOf(out, "Campaign published"), "go");
    setPubNotes("");
    setPubFw("");
    campsLive.reload && campsLive.reload();
  };

  if (posture.loading && !posture.data) {
    return html`<div className="ota-desk ota-desk--liquid" data-cmc-ota="page49"
      data-cmc-page="page103-ota" data-cmc-residual="page204-ota" data-cmc-ota158="page158-ota">
      <p className="note">Reading Base image seat… Catalogue stays empty until the server answers.</p>
    </div>`;
  }

  if (!otaOk) {
    return html`<div className="ota-desk ota-desk--liquid" data-cmc-ota="page49"
      data-cmc-page="page103-ota" data-cmc-residual="page204-ota" data-cmc-ota158="page158-ota">
      <div className="ota-desk__rail">
        <div>
          <p className="eyebrow">OTA · Base image</p>
          <h3 className="ota-desk__title">Base image · seat closed</h3>
          <p className="note">${postureLabel}. Campaigns stay empty while unarmed.</p>
        </div>
      <div className="btn-row ota-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act" onClick=${reload}
          disabled=${posture.loading || !seat.reReadAllowed}>Re-read</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/ota")}>
          Open OTA primary
        </button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/edge/skills")}>
          Skills warehouse
        </button>
      </div>
      </div>
      <${Alert} tone="info" title="Base image posture"
        say=${"Lifecycle " + (seat.lifecycleAllowed ? "open" : "sealed")
          + " · publish " + (seat.publishAllowed ? "open" : "sealed")
          + " · " + (seat.role || "—")}/>
      <${Card} title="Base image posture" meta="status">
        ${posture.error ? html`<${Alert} tone="warn" title="Could not refresh status"
          say=${softSettingsOtaErrorOf(posture.error)}/>` : null}
        ${!posture.error ? html`
          <div className="ota-desk__kpis" role="group" aria-label="OTA posture gated">
            ${[
              ["Lifecycle", "sealed", "0", "admin seat"],
              ["Public base", seat.publicBase ? "set" : "—", seat.publicBase ? "1" : "0", "device URL"],
              ["Token", seat.token ? "set" : "—", seat.token ? "1" : "0", "download guard"],
              ["Publish", canPub ? "open" : "sealed", canPub ? "1" : "0", "staged builds"],
            ].map(([k, v, on, em]) => html`
              <div key=${k} className="ota-desk__kpi" data-on=${on}>
                <span>${k}</span><b>${v}</b><em style=${{ fontSize:11,color:"var(--fg-3)",fontStyle:"normal" }}>${em}</em>
              </div>`)}
          </div>
          <p className="note">${postureLabel}</p>` : null}
      <//>
      <${Alert} tone="info" title="Base image grant required"
        say=${"Campaigns need a manager or OTA admin seat. Local campaign lists are not invented when the seat is closed."}/>
    </div>`;
  }

  const buildsDenied = !!(buildsLive.error && /\b40[13]\b|forbidden/i.test(String(buildsLive.error || "")));

  return html`<div className="ota-desk ota-desk--liquid desk-craft" data-cmc-ota="page49"
    data-cmc-page="page103-ota" data-cmc-residual="page204-ota" data-cmc-ota158="page158-ota"
    data-ota-rail="board">
    <div className="ota-desk__rail">
      <div>
        <p className="eyebrow">OTA · Base image</p>
        <h3 className="ota-desk__title">Board campaigns</h3>
        <p className="note ota-desk__lede">
          Full-board image campaigns only · publish needs superadmin or OTA admin.
          Capability packs live on the Capabilities track or Skills warehouse — never from this desk.
        </p>
      </div>
      <div className="btn-row ota-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          disabled=${act.busy || campsLive.loading || posture.loading || !seat.reReadAllowed}
          onClick=${reload}>
          <${Ico} d="refresh" w=${13}/>Re-read
        </button>
        <button type="button" className="btn btn--go btn--sm desk-craft__act" onClick=${() => go("/ota")}>
          <${Ico} d="cpu" w=${13}/>OTA tracks
        </button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/edge/skills")}>
          Skills warehouse
        </button>
      </div>
    </div>
    <${Alert} tone="warn" title="Base image · not skills"
      say="Campaigns here reflash whole board images. Capability packs use the Capabilities track or Edge Skills warehouse."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Alert} tone="info" title="Base image posture"
      say=${postureLabel
        + " · write " + (seat.writeAllowed ? "open" : "sealed")
        + " · " + (seat.role || "—")}/>
    ${!seat.fanoutAllowed && (seat.consentAllowed || seat.writeAllowed)
      ? html`<${Alert} tone="info" title="Tenant consent"
          say="Accept records consent only. Base firmware dispatch needs an OTA admin seat."/>`
      : null}

    <${Card} title="Base image posture" meta="status" cls="mt">
      ${posture.error ? html`<${Alert} tone="warn" title="Could not refresh status"
        say=${softSettingsOtaErrorOf(posture.error) + " Nothing is invented when the seat is closed."}/>` : null}
      ${!posture.error ? html`
        <div className="ota-desk__kpis" role="group" aria-label="OTA posture">
          ${[
            ["Lifecycle", otaOk ? "open" : "sealed", otaOk ? "1" : "0", "campaigns"],
            ["Public base", seat.publicBase ? "set" : "missing", seat.publicBase ? "1" : "0", "device URL"],
            ["Token", seat.token ? "set" : "missing", seat.token ? "1" : "0", "download guard"],
            ["Publish", canPub ? "open" : "sealed", canPub ? "1" : "0", "staged builds"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="ota-desk__kpi" data-on=${on}>
              <span>${k}</span><b>${v}</b>
              <em style=${{ fontSize:11,color:"var(--fg-3)",fontStyle:"normal",display:"block",marginTop:2 }}>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          campaign write ${seat.writeAllowed ? "open" : "sealed"}
          · store read ${post.campaigns_pg_read ? "on" : "off"}
          · Catalogue stays empty until the server returns rows.
        </p>` : null}
    <//>

    <div className="ota-desk__kpis">
      <div className="ota-desk__kpi" data-on=${camps.length ? "1" : "0"}>
        <span>Campaigns</span><b>${campsLive.loading ? "…" : camps.length}</b>
        <em>lifecycle</em>
      </div>
      <div className="ota-desk__kpi" data-on=${pol.writes ? "1" : "0"}>
        <span>Writes</span><b>${policyLive.loading ? "…" : (pol.writes ? "armed" : "—")}</b>
        <em>${seat.writeAllowed ? "open" : "sealed"}</em>
      </div>
      <div className="ota-desk__kpi" data-on=${pol.autoRb ? "1" : "0"}>
        <span>Auto roll-back</span><b>${policyLive.loading ? "…" : (pol.autoRb ? "on" : "off")}</b>
        <em>env</em>
      </div>
      <div className="ota-desk__kpi" data-on=${canPub ? "1" : "0"}>
        <span>Publish</span><b>${canPub ? "open" : "sealed"}</b>
        <em>builds</em>
      </div>
    </div>

    <${Card} title="Campaigns" meta=${campsLive.loading ? "reading…" : (camps.length + " loaded")}
        sub="Accept · Decline · Roll back · Cancel are real writes when your seat allows them. Buttons stay locked until your seat opens them.">
      <div className="ota-desk__filters" role="group" aria-label="Campaign filter">
        ${CAMPAIGN_FILTERS.map((f) => html`
          <button key=${f.id} type="button" className="chip" data-tone="ai"
            data-sel=${filter === f.id ? "1" : "0"}
            onClick=${() => setFilter(f.id)}>
            ${f.label}${f.id === "all" ? " · " + camps.length : " · " + count(f.id)}
          </button>`)}
      </div>
      ${campsLive.error
        ? html`<${Alert} tone="warn" title="Could not load campaigns"
          say=${softSettingsOtaErrorOf(campsLive.error) + " Nothing is invented when the seat is closed."}/>`
        : null}
      ${!campsLive.loading && !filtered.length
        ? html`<p className="note">No campaigns under this filter.</p>`
        : null}
      <div className="rows">
        ${filtered.map((c) => html`
          <div key=${c.id} className="row ota-desk__camp" data-state=${c.state === "accepted" ? "on" : "alarm"}>
            <span className="st"/>
            <span className="main">
              <b>${c.fw}</b>
              <span className="m">${c.id} · ${c.state} · by ${c.by}</span>
              ${c.notes ? html`<span>${c.notes}</span>` : null}
            </span>
            <div className="ota-desk__acts">
              <button type="button" className="btn btn--go btn--xs"
                disabled=${act.busy || !(seat.fanoutAllowed || seat.consentAllowed || seat.writeAllowed)}
                onClick=${() => liveWrite("accept", c.id)}>${seat.fanoutAllowed ? "Accept" : "Accept · consent"}</button>
              <button type="button" className="btn btn--ghost btn--xs"
                disabled=${act.busy || !seat.writeAllowed}
                onClick=${() => liveWrite("decline", c.id)}>Decline</button>
              <button type="button" className="btn btn--ghost btn--xs"
                disabled=${act.busy || !seat.fanoutAllowed}
                onClick=${() => liveWrite("rollback", c.id)}>Roll back</button>
              <button type="button" className="btn btn--danger btn--xs"
                disabled=${act.busy || !seat.fanoutAllowed}
                onClick=${() => cancel(c.id)}>Cancel</button>
            </div>
          </div>`)}
      </div>
    <//>

    <div className="ota-desk__grid">
      <${Card} title="OTA policy" meta="deployment"
          sub="Deployment environment readout. These values do not change roll-back or stall policy from the browser alone.">
        ${policyLive.loading
          ? html`<p className="note">Reading policy…</p>`
          : policyLive.error
            ? html`<${Alert} tone="warn" title="Could not load policy"
              say=${softSettingsOtaErrorOf(policyLive.error)}/>`
            : html`<${F}>
                <${KV} k="Auto-rollback on failure" v=${pol.autoRb ? "on" : "off"}
                  tone=${pol.autoRb ? "go" : "dim"}/>
                <${KV} k="Device ack timeout"
                  v=${pol.ackS != null ? (pol.ackS + " s") : "—"}/>
                <${KV} k="Hardware match" v=${pol.hw ? "on" : "off"}/>
                <${KV} k="Campaign reads" v=${pol.reads ? "armed" : "off"}/>
                <${KV} k="Campaign writes" v=${pol.writes ? "armed" : "off"}/>
                <${KV} k="Public OTA base" v=${pol.base ? "configured" : "missing"}
                  tone=${pol.base ? "go" : "warn"}/>
                <${KV} k="OTA token" v=${pol.token ? "configured" : "missing"}
                  tone=${pol.token ? "go" : "warn"}/>
                ${pol.note ? html`<p className="note">${pol.note}</p>` : null}
              <//>`}
      <//>

      <${Card} title="Builds + publish" meta=${canPub ? "publish" : "publish sealed"}
          sub=${canPub
            ? "Publish creates a campaign from a file already staged on the OTA server."
            : "Staged inventory needs superadmin or OTA admin. Managers can still accept or decline campaigns."}>
        ${!canPub
          ? html`<p className="note">This grant manages campaign lifecycle, not the staged inventory. Builds only from the staged inventory.</p>`
          : buildsDenied
            ? html`<${Alert} tone="warn" title="Could not load builds"
              say=${softSettingsOtaErrorOf(buildsLive.error)}/>`
            : html`<${F}>
                ${buildsLive.loading
                  ? html`<p className="note">Reading builds…</p>`
                  : !builds.length
                    ? html`<p className="note">No staged artifacts.</p>`
                    : html`<div className="rows" style=${{ marginBottom: 10 }}>
                        ${builds.slice(0, 10).map((b) => html`
                          <div key=${b.name} className="row row--say">
                            <${Ico} d="cpu" w=${14}/>
                            <span className="main">
                              <b>${b.fw}</b>
                              <span className="m">${b.name} · ${b.size} · ${b.sha}</span>
                            </span>
                          </div>`)}
                      </div>`}
                <${Field} label="Staged file">
                  <select className="inp" value=${pubFile}
                    onChange=${(e) => {
                      const v = e.target.value;
                      setPubFile(v);
                      const hit = builds.find((b) => b.name === v);
                      if (hit && hit.fw) setPubFw(String(hit.fw));
                    }}>
                    <option value="">Select a .bin…</option>
                    ${builds.map((f) => html`
                      <option key=${f.name} value=${f.name}>${f.name}${f.fw ? (" · " + f.fw) : ""}</option>`)}
                  </select>
                <//>
                <${Field} label="Firmware version (optional)">
                  <${Input} value=${pubFw} onChange=${(e) => setPubFw(e.target.value)}
                    placeholder="from .version / filename"/>
                <//>
                <${Field} label="Notes (optional)">
                  <${Input} value=${pubNotes} onChange=${(e) => setPubNotes(e.target.value)}
                    placeholder="rollout note"/>
                <//>
                <button type="button" className="btn btn--go btn--sm"
                  disabled=${act.busy || !pubFile.trim() || !seat.publishAllowed}
                  onClick=${publishStored}>Publish campaign</button>
              <//>`}
      <//>
    </div>
  </div>`;
}
